import Chip from '@mui/material/Chip';
import { priorityColor } from '../../shared/constants';
import type { WorkloadStatus } from '../tasks.types';
import { humanize, WORKLOAD_COLORS } from '../tasks.utils';

/** A lookup-coloured chip (task status, priority, reassignment status). */
export function ColorChip({ label, color }: { label: string | null | undefined; color?: string | null }) {
  if (!label) return null;
  return (
    <Chip
      size='small'
      label={label}
      sx={{
        fontWeight: 500,
        bgcolor: color ? `${color}22` : undefined,
        color: color ?? undefined,
        border: color ? `1px solid ${color}66` : undefined,
      }}
    />
  );
}

export function WorkloadChip({ status }: { status: WorkloadStatus | null | undefined }) {
  if (!status) return null;
  return <Chip size='small' variant='outlined' color={WORKLOAD_COLORS[status]} label={humanize(status)} />;
}

const STATE_COLORS: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info' | 'primary'> = {
  REQUESTED: 'warning',
  APPROVED: 'info',
  PENDING_ACCEPTANCE: 'warning',
  ACTIVE: 'primary',
  ACCEPTED: 'success',
  REASSIGNED: 'success',
  COMPLETED: 'success',
  RELEASED: 'default',
  REJECTED: 'error',
  DECLINED: 'error',
  CANCELLED: 'default',
  DRAFT: 'default',
  SUBMITTED: 'warning',
};

/** Workflow state of an assignment, reassignment or timesheet. */
export function StateChip({ status, label }: { status: string | null | undefined; label?: string | null }) {
  if (!status) return null;
  return <Chip size='small' variant='outlined' color={STATE_COLORS[status] ?? 'default'} label={label || humanize(status)} />;
}

/** A priority in the app-wide priority palette (red High, yellow Medium, green Low). */
export function PriorityChip({ code, label, fallbackColor }: { code: string | null | undefined; label: string | null | undefined; fallbackColor?: string | null }) {
  return <ColorChip label={label} color={priorityColor(code) ?? fallbackColor} />;
}
